import type { ScopeNode } from "../../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import type { TreeNode } from "../../../../Components/Tree/types";
import { isUserEditableNode } from "../../../../Components/Tree/TreeUtils";
import type { NameCollision, ScopeNodeType } from "./types";

// Nearest parent first, root last.
export const findAncestorIds = (
  root: ScopeNode | null | undefined,
  id: string
): string[] => {
  const walk = (node: ScopeNode, above: string[]): string[] | null => {
    if (node.id === id) {
      return above;
    }
    for (const child of node.children ?? []) {
      const found = walk(child, [node.id, ...above]);
      if (found) {
        return found;
      }
    }
    return null;
  };
  return root ? (walk(root, []) ?? []) : [];
};

export const editableMembersTree = (
  root: ScopeNode | null | undefined
): TreeNode[] | null => {
  if (!root) {
    return null;
  }
  const children = (root.children ?? []).filter(isUserEditableNode);
  return [{ ...root, children }] as unknown as TreeNode[];
};

export const withoutSubtree = (
  root: ScopeNode | null | undefined,
  id: string
): ScopeNode | null => {
  if (!root || root.id === id) {
    return null;
  }
  const prune = (node: ScopeNode): ScopeNode => ({
    ...node,
    children: node.children?.filter((child) => child.id !== id).map(prune),
  });
  return prune(root);
};

// ScopeNode and TreeNode describe the same objects, but TS won't assign one to
// the other (interface vs index signature); the Tree reads only shared fields.
export const asTreeNode = (
  node: ScopeNode | null | undefined
): TreeNode | null => (node ?? null) as unknown as TreeNode | null;
export const asTreeNodes = (nodes: ScopeNode[]): TreeNode[] =>
  nodes as unknown as TreeNode[];

export const IN_USE_TOOLTIP =
  "Linked to active rules, automations, or widgets.";

const COLLISION_NOUN: Record<ScopeNodeType, string> = {
  scope: "scopes",
  group: "scope groups",
};

// Lists the colliding names unless the only one is the node's own.
export const collisionMessage = (
  collisions: NameCollision[],
  nodeType: ScopeNodeType,
  ownName?: string
): string => {
  const type = collisions[0]?.node_type ?? nodeType;
  const base = `Two ${COLLISION_NOUN[type]} with the same name can't exist at the same location.`;
  const others = [...new Set(collisions.map((c) => c.name))].filter(
    (name) => name !== ownName
  );
  return others.length ? `${base} (${others.join(", ")})` : base;
};
